class YipTimeline {
  constructor(sectionElement) {
    if (sectionElement.yipTimelineInstance) return;
    sectionElement.yipTimelineInstance = this;
    this.section = sectionElement;
    this.sectionId = this.section.dataset.sectionId;
    if (!this.sectionId) return;
    this.initNodes();
    if (!this.modal || !this.toggleButtons.length) return;
    this.initData();
    this.initListeners();
  }

  initNodes() {
    this.introContainer = this.section.querySelector('.yip-timeline-intro');
    this.timelineContainer = this.section.querySelector(`#yip-timeline-container-${this.sectionId}`);
    this.toggleButtons = this.section.querySelectorAll('.yip-timeline-toggle-btn');
    this.modalContainer = this.section.querySelector(`#yip-timeline-modal-container-${this.sectionId}`);
    this.modal = this.modalContainer?.querySelector('.yip-timeline-modal');
    this.modalTitle = this.modal?.querySelector('.yip-timeline-modal-title');
    this.modalContent = this.modal?.querySelector('.yip-timeline-modal-content');
    this.modalLogoContainer = this.modal?.querySelector('.yip-timeline-modal-logo');
    this.closeButtons = this.modal?.querySelectorAll('.yip-timeline-modal-close, .yip-timeline-modal__continue');
    this.dragHandles = this.modal?.querySelectorAll('.yip-timeline-modal-header, .yip-timeline-modal-drag-handle');
    this.timelineContents = this.section.querySelectorAll('.yip-timeline-content');
  }

  initData() {
    this.modalContents = {};
    this.section.querySelectorAll('.yip-timeline-branch').forEach(branch => {
      const blockId = branch.dataset.blockId;
      if (!blockId) return;
      this.modalContents[blockId] = {
        title: branch.querySelector('.yip-timeline-data-title')?.textContent?.trim() || '',
        content: branch.querySelector('.yip-timeline-data-content')?.innerHTML?.trim() || '',
        logo: branch.querySelector('.yip-timeline-data-logo')?.innerHTML?.trim() || '',
      };
    });
    this.lastOpener = null;
    this.animationDuration = 2000;
    this.isAnimating = false;
  }

  toggleTimeline() {
    if (this.isAnimating) return;
    this.isAnimating = true;

    const isExpanded = this.section.classList.contains('timeline-is-expanded');
    
    if (!isExpanded) {
      this.section.classList.add('timeline-is-expanded');
      this.toggleButtons.forEach(btn => btn.setAttribute('aria-expanded', 'true'));

      setTimeout(() => {
        this.timelineContents.forEach(content => {
          content.classList.add('animate-in');
        });
      }, 50);

    } else {
      this.timelineContents.forEach(content => {
        content.classList.remove('animate-in');
      });
      
      this.section.classList.remove('timeline-is-expanded');
      this.toggleButtons.forEach(btn => btn.setAttribute('aria-expanded', 'false'));
      this.scrollToIntroCenter();
    }
    
    setTimeout(() => {
      this.isAnimating = false;
    }, this.animationDuration);
  }

  scrollToIntroCenter() {
    if (!this.introContainer) return;
    const introRect = this.introContainer.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const introHeight = introRect.height;
    const targetPosition = window.pageYOffset + introRect.top - (viewportHeight / 2) + (introHeight / 2);
    window.scrollTo({ top: Math.max(0, targetPosition), behavior: 'auto' });
  }

  showModal(id, opener) {
    if (this.modalContainer.classList.contains('is-visible') || this.isAnimating) return;
    const data = this.modalContents[id];
    if (!data) return;
    this.isAnimating = true;
    this.lastOpener = opener;
    this.modalTitle.textContent = data.title;
    this.modalContent.innerHTML = data.content;
    this.modalLogoContainer.innerHTML = data.logo;
    this.modalContainer.classList.add('is-visible');
    this.lockBodyScroll();
    setTimeout(() => {
      this.modal.focus();
      this.isAnimating = false;
    }, 50);
  }

  closeModal() {
    if (!this.modalContainer.classList.contains('is-visible') || this.isAnimating) return;
    this.isAnimating = true;
    this.modalContainer.classList.remove('is-visible');
    this.unlockBodyScroll();
    setTimeout(() => {
      const fallbackFocus = this.section.querySelector('.yip-timeline-toggle-btn') || this.section;
      (this.lastOpener || fallbackFocus)?.focus();
      this.lastOpener = null;
      this.isAnimating = false;
    }, 100);
  }

  lockBodyScroll() {
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.paddingRight = `${scrollbarWidth}px`;
    document.documentElement.classList.add('yip-timeline-html-is-locked');
    document.body.classList.add('yip-timeline-body-is-locked');
  }

  unlockBodyScroll() {
    document.body.style.paddingRight = '';
    document.documentElement.classList.remove('yip-timeline-html-is-locked');
    document.body.classList.remove('yip-timeline-body-is-locked');
  }

  handleFocusTrap(e) {
    if (e.key !== 'Tab' || !this.modalContainer.classList.contains('is-visible')) return;
    const focusable = Array.from(this.modal.querySelectorAll('button:not([disabled]), [href]:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]):not([disabled])'));
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      last.focus();
      e.preventDefault();
    } else if (!e.shiftKey && document.activeElement === last) {
      first.focus();
      e.preventDefault();
    }
  }

  initListeners() {
    this.toggleButtons.forEach(btn => {
      btn.addEventListener('click', this.toggleTimeline.bind(this));
    });
    this.section.querySelectorAll('[data-modal-id]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.showModal(btn.dataset.modalId, btn);
      });
      btn.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.showModal(btn.dataset.modalId, btn);
        }
      });
    });
    this.closeButtons.forEach(btn => {
      btn.addEventListener('click', this.closeModal.bind(this));
    });
    this.modalContainer.addEventListener('click', (e) => {
      if (e.target === this.modalContainer) this.closeModal();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.modalContainer.classList.contains('is-visible')) {
        this.closeModal();
      }
      this.handleFocusTrap(e);
    });
    this.initDragToClose();
  }

  initDragToClose() {
    let state = { dragging: false, startY: 0, currentY: 0, threshold: 100, startTime: 0 };
    if (!this.dragHandles) return;
    this.dragHandles.forEach(handle => {
      handle.addEventListener('touchstart', (e) => {
        if (e.touches.length !== 1 || window.innerWidth >= 1200) return;
        state.startY = e.touches[0].clientY;
        state.startTime = Date.now();
        state.dragging = true;
        this.modal.style.transition = 'none';
      }, { passive: true });
    });
    document.addEventListener('touchmove', (e) => {
      if (!state.dragging) return;
      e.preventDefault();
      state.currentY = e.touches[0].clientY - state.startY;
      if (state.currentY > 0) {
        const resistance = Math.min(state.currentY / 2, state.currentY);
        this.modal.style.transform = `translateY(${resistance}px)`;
      }
    }, { passive: false });
    document.addEventListener('touchend', () => {
      if (!state.dragging) return;
      state.dragging = false;
      const dragDuration = Date.now() - state.startTime;
      const velocity = state.currentY / dragDuration;
      this.modal.style.transition = `transform 400ms cubic-bezier(0.4, 0, 0.2, 1)`;
      const shouldClose = state.currentY > state.threshold || velocity > 0.5;
      if (shouldClose) {
        this.modal.style.transform = 'translateY(100%)';
        setTimeout(() => {
          this.closeModal();
          this.modal.style.transform = '';
          this.modal.style.transition = '';
        }, 400);
      } else {
        this.modal.style.transform = '';
        setTimeout(() => this.modal.style.transition = '', 400);
      }
      state.currentY = 0;
    }, { passive: true });
  }

  destroy() {
    this.section.yipTimelineInstance = null;
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.yip-timeline-section').forEach(section => {
    try {
      new YipTimeline(section);
    } catch (error) {
      console.warn('Failed to initialize timeline:', error);
    }
  });
  
  if (window.MutationObserver) {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1 && node.classList?.contains('yip-timeline-section')) {
            new YipTimeline(node);
          }
        });
      });
    });
    
    observer.observe(document.body, { childList: true, subtree: true });
  }
});